import { useCallback, useId, useMemo } from 'react';

import type { CameraInfo } from '@scaleaq/sitecontrol-lib';

import { ModalHeader } from '@scaleaq/scaleui';
import { Pin, PinOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CameraStreamWidget } from '@/components/CameraStreamWidget.tsx';
import type { CameraSwitching } from '@/components/cameraStreamWidget/cameraSwitchPanel/cameraSwitchPanel.types.ts';
import { runAfterExitingFullscreen } from '@/components/cameraStreamWidget/useCameraStreamFullscreen.ts';
import {
  CameraSelector,
  CameraWidgetConfig,
} from '@/components/globalCameraStreamWidget/CameraSelector.tsx';
import { resolveCameraWidgetSelection } from '@/components/globalCameraStreamWidget/globalCameraStreamWidget.functions.ts';
import { useGlobalCameraStreamWidgetGamepad } from '@/components/globalCameraStreamWidget/useGlobalCameraStreamWidgetGamepad.ts';
import { GridWidget } from '@/components/GridWidget.tsx';
import { useFetchCameras, useInventory } from '@/dataAccess/rest';
import { useLayoutProps, useOpenModal } from '@/hooks';
import { useLandingPageContext } from '@/pages/landingPage/landingPageProvider/LandingPageContext.tsx';
import { stampCameraSelectorToggleTime } from '@/providers/gamepadProvider/gamepadProvider.help.ts';
import { FixedHeightStyledModal } from '@/styles.ts';
import { GenericWidgetProps, WidgetHealthIssue } from '@/types.ts';
import { Icon } from '@/utils';
import { CAMERA_STREAM_WIDGET } from '@/widgetComponentNames';

interface GlobalCameraStreamWidgetProps extends GenericWidgetProps {
  config?: CameraWidgetConfig;
  widgetId: string;
}

export const GlobalCameraStreamWidget = ({
  config: configInput,
  widgetId,
  ...rest
}: GlobalCameraStreamWidgetProps) => {
  const { t } = useTranslation(['cameraControl', 'common', 'landingPage']);
  const { data: cameras, isLoading, isError } = useFetchCameras();

  const { config } = useLayoutProps<CameraWidgetConfig>({
    configInput,
    defaultConfig: { camera: { id: '', name: '' } },
    widgetId,
  });
  const { updateWidgetConfigAndSave } = useLandingPageContext();
  const topBannerPinned = config.topBannerPinned ?? false;
  const isCameraNameVisible = config.isCameraNameVisible ?? false;

  const { camera: foundCamera, siblingCameras } = useMemo(
    () => resolveCameraWidgetSelection(cameras, config),
    [cameras, config]
  );

  // Same query as the one CameraStreamWidget runs, so this resolves the pen name without a
  // second request — see the loading placeholder title below.
  const { data: parent } = useInventory(foundCamera?.parentId ?? config.camera.parentId);

  const editId = useId();
  const pinToolbarActionId = useId();
  const showCameraNameToolbarActionId = useId();
  const modalController = useOpenModal();
  const isModalOpen = modalController.open;
  const openModal = modalController.handleOpenModal;
  const closeModalRaw = modalController.onClose;

  const refocusCameraWidget = useCallback(() => {
    const target = getCameraWidgetRootByWidgetId(widgetId);
    if (!target) return;
    target.focus({ preventScroll: false });
    if (!target.matches(':focus-visible')) {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [widgetId]);

  const openModalExitingFullscreen = useCallback(() => {
    runAfterExitingFullscreen(openModal);
  }, [openModal]);

  const closeModal = useCallback(() => {
    closeModalRaw();
    // Stamp the toggle time so the debounce window restarts from now.
    // Without this, a still-held LeftStick.Press would be re-detected as a
    // fresh press after the input-map swap (releaseAll clears consumed state)
    // and immediately reopen the modal.
    stampCameraSelectorToggleTime();
    // Modal teardown can temporarily steal focus; restore focus on next turn.
    window.setTimeout(() => {
      refocusCameraWidget();
    }, 0);
  }, [closeModalRaw, refocusCameraWidget]);

  useGlobalCameraStreamWidgetGamepad({
    widgetId,
    isModalOpen,
    openModal: openModalExitingFullscreen,
    closeModal,
  });

  /**
   * Switching camera, stream profile, camera-name visibility, or top-banner pin is playback
   * preference, not layout design, so it is saved right away instead of leaving the dashboard
   * in edit mode.
   */
  const persistCamera = useCallback(
    (camera: CameraWidgetConfig['camera']) => {
      updateWidgetConfigAndSave({ widgetId, config: { camera } });
    },
    [updateWidgetConfigAndSave, widgetId]
  );

  const handlePersistWebRtcStreamName = useCallback(
    (selectedStreamName: string | undefined) => {
      // Skip layout update when unchanged (incl. both undefined) to avoid a pointless save.
      if (selectedStreamName === config.camera.selectedStreamName) return;
      persistCamera({ ...config.camera, selectedStreamName });
    },
    [config.camera, persistCamera]
  );

  const handleSelectSiblingCamera = useCallback(
    (camera: CameraInfo) => {
      persistCamera({
        ...config.camera,
        id: camera.id,
        name: camera.name,
        parentId: camera.parentId,
      });
    },
    [config.camera, persistCamera]
  );

  const handleCycleCamera = useCallback(() => {
    if (!foundCamera || siblingCameras.length < 2) return;
    const currentIndex = siblingCameras.findIndex(camera => camera.id === foundCamera.id);
    const nextCamera = siblingCameras[(currentIndex + 1) % siblingCameras.length];
    handleSelectSiblingCamera(nextCamera);
  }, [foundCamera, handleSelectSiblingCamera, siblingCameras]);

  const cameraSwitching = useMemo<CameraSwitching>(
    () => ({
      siblings: siblingCameras,
      onSelect: handleSelectSiblingCamera,
      onCycle: handleCycleCamera,
      onOpenSelector: openModalExitingFullscreen,
    }),
    [siblingCameras, handleSelectSiblingCamera, handleCycleCamera, openModalExitingFullscreen]
  );

  const toggleTopBannerPinned = useCallback(() => {
    updateWidgetConfigAndSave({
      widgetId,
      config: { topBannerPinned: !topBannerPinned },
    });
  }, [topBannerPinned, updateWidgetConfigAndSave, widgetId]);

  const toggleCameraNameVisible = useCallback(() => {
    updateWidgetConfigAndSave({
      widgetId,
      config: { isCameraNameVisible: !isCameraNameVisible },
    });
  }, [isCameraNameVisible, updateWidgetConfigAndSave, widgetId]);

  const actionList = useMemo(
    () => [
      {
        id: editId,
        label: t('landingPage:switchCamera'),
        onClick: openModalExitingFullscreen,
        Icon: Icon.SwitchCamera,
      },
    ],
    [editId, openModalExitingFullscreen, t]
  );

  const actionListWithPinAction = useMemo(
    () => [
      ...actionList,
      {
        id: pinToolbarActionId,
        label: topBannerPinned
          ? t('cameraControl:unpinTopBanner')
          : t('cameraControl:pinTopBanner'),
        onClick: toggleTopBannerPinned,
        Icon: topBannerPinned ? PinOff : Pin,
      },
      {
        id: showCameraNameToolbarActionId,
        label: isCameraNameVisible
          ? t('cameraControl:hideCameraName')
          : t('cameraControl:showCameraName'),
        onClick: toggleCameraNameVisible,
        Icon: isCameraNameVisible ? Icon.EyeClosed : Icon.Eye,
      },
    ],
    [
      actionList,
      pinToolbarActionId,
      showCameraNameToolbarActionId,
      topBannerPinned,
      isCameraNameVisible,
      t,
      toggleTopBannerPinned,
      toggleCameraNameVisible,
    ]
  );

  const configuredCameraName = config.camera.name;
  const cameraName = foundCamera?.name ?? configuredCameraName;
  // The placeholder shown while cameras load must name the widget the same way the stream
  // header will, or the camera name flashes before the pen name takes over. An unresolved pen
  // name stays blank rather than falling back to the camera name, for the same reason.
  const loadingTitle = isCameraNameVisible ? cameraName : (parent?.name ?? '');

  const healthIssue = useMemo<WidgetHealthIssue[]>(() => {
    if (isLoading) return [];
    if (isError) {
      return [
        {
          severity: 'error',
          message: t('cannotFetchCameras'),
        },
      ];
    }
    if (!foundCamera) {
      return [
        {
          severity: 'critical',
          message: t('cameraNotFound', { name: configuredCameraName }),
        },
      ];
    }
    return [];
  }, [configuredCameraName, isLoading, isError, foundCamera, t]);

  return (
    <>
      {isLoading || isError || !foundCamera ? (
        <GridWidget
          {...rest}
          widgetId={widgetId}
          toolbarActionList={actionList}
          headerTitle={isLoading ? loadingTitle : cameraName}
          isLoading={isLoading}
          isError={!isLoading && (isError || !foundCamera)}
          errorMessage={
            isLoading
              ? undefined
              : isError
                ? t('cannotFetchCameras')
                : t('cameraNotFound', { name: configuredCameraName })
          }
          healthIssues={healthIssue}
          onContextMenu={openModalExitingFullscreen}
        />
      ) : (
        <CameraStreamWidget
          {...rest}
          widgetId={widgetId}
          camera={foundCamera}
          editActionList={actionListWithPinAction}
          persistedWebRtcStreamName={config.camera.selectedStreamName}
          onPersistWebRtcStreamName={handlePersistWebRtcStreamName}
          topBannerPinned={topBannerPinned}
          isCameraNameVisible={isCameraNameVisible}
          onContextMenu={openModalExitingFullscreen}
          cameraSwitching={cameraSwitching}
        />
      )}
      <FixedHeightStyledModal
        open={isModalOpen}
        onClose={closeModal}
        closeOnBackdropClick={true}
        closeOnEscapeClick={true}
      >
        <ModalHeader title={t('landingPage:cameras')} onClose={closeModal} />
        <CameraSelector
          widgetId={widgetId}
          onSave={closeModal}
          onClose={closeModal}
          focusSelectedCamera
          isOpen={isModalOpen}
        />
      </FixedHeightStyledModal>
    </>
  );
};

const getCameraWidgetRootByWidgetId = (widgetId: string): HTMLElement | null => {
  return document.querySelector<HTMLElement>(
    `.grid-stack-item[gs-id="${widgetId}"] [data-component="${CAMERA_STREAM_WIDGET}"]`
  );
};
